<app-app-bar></app-app-bar>

<app-main-content>
  <div class="lens-page lens-insight">
    <section class="dash-container insight-header lens-banner">
      <div>
        <h1 class="dash-section-head__title">Insight</h1>
        <p class="dash-section-head__subtitle">Your test history and campus-wide network trends</p>
      </div>
      <div class="insight-view-toggle lens-toggle" role="tablist" aria-label="Insight view">
        <button type="button" class="insight-view-toggle__btn"
          [class.insight-view-toggle__btn--active]="activeView() === 'personal'"
          role="tab" [attr.aria-selected]="activeView() === 'personal'"
          (click)="setView('personal')">Personal</button>
        <button type="button" class="insight-view-toggle__btn"
          [class.insight-view-toggle__btn--active]="activeView() === 'aggregate'"
          role="tab" [attr.aria-selected]="activeView() === 'aggregate'"
          (click)="setView('aggregate')">Aggregate</button>
      </div>
    </section>

    @if (activeView() === 'personal') {
    <section class="dash-container insight-panel" aria-live="polite">
      @if (!clientId) {
      <div class="insight-empty">
        <mat-icon>history</mat-icon>
        <h2>No device history yet</h2>
        <p>Your completed tests will appear here after you run a test on this device.</p>
      </div>
      } @else if (personalError() && !personalTests().length) {
      <div class="insight-empty insight-empty--error" role="alert">
        <mat-icon>cloud_off</mat-icon>
        <h2>We couldn’t load your insights</h2>
        <p>Check your connection and try again.</p>
        <button type="button" class="insight-action" (click)="loadPersonal(true)">Try again</button>
      </div>
      } @else if (personalLoading() && !personalTests().length) {
      <div class="insight-empty"><span class="insight-spinner"></span><p>Loading your results…</p></div>
      } @else if (!personalTests().length) {
      <div class="insight-empty">
        <mat-icon>query_stats</mat-icon>
        <h2>No completed tests found</h2>
        <p>Results from valid, completed tests will appear here.</p>
      </div>
      } @else {
      <header class="insight-list-head">
        <div><h2>Recent tests</h2><p>{{ personalTotal() }} completed test{{ personalTotal() === 1 ? '' : 's' }}</p></div>
      </header>
      <div class="insight-table-scroll" role="region" aria-label="Recent tests" tabindex="0">
        <table class="insight-table">
          <thead><tr><th scope="col">Test</th><th scope="col">Download</th><th scope="col">Upload</th><th scope="col">Ping</th><th scope="col">Network</th><th scope="col">QoE score</th><th scope="col">Date</th></tr></thead>
          <tbody>
          @for (test of personalTests(); track test.test_id) {
            <tr>
              <td data-label="Test"><b>{{ test.spot_name || 'Unassigned campus location' }}</b><span>{{ test.location_area || 'Location not recorded' }} · {{ test.relative_timestamp }}</span></td>
              <td data-label="Download" class="insight-table__metric">{{ formatSpeed(test.download_speed) }}<em>Mbps</em></td>
              <td data-label="Upload" class="insight-table__metric">{{ formatSpeed(test.upload_speed) }}<em>Mbps</em></td>
              <td data-label="Ping" class="insight-table__metric">{{ formatSpeed(test.ping) }}<em>ms</em></td>
              <td data-label="Network"><span class="lens-badge">{{ test.network_type || "—" }}</span></td>
              <td data-label="QoE score"><span class="lens-badge" [class.lens-badge--excellent]="test.qoe_classification === 'Excellent'" [class.lens-badge--good]="test.qoe_classification === 'Good'" [class.lens-badge--fair]="test.qoe_classification === 'Fair'" [class.lens-badge--poor]="test.qoe_classification === 'Poor'"><strong>{{ test.qoe_score == null ? '—' : test.qoe_score.toFixed(2) }}</strong> · {{ test.qoe_classification || 'Not classified' }}</span></td>
              <td data-label="Date"><time [attr.datetime]="test.measured_at">{{ test.measured_at | date:'medium' }}</time></td>
            </tr>
          }
          </tbody>
        </table>
      </div>
      @if (hasMorePersonal()) {
      <button type="button" class="insight-action insight-action--wide" [disabled]="personalLoading()" (click)="loadPersonal()">
        {{ personalLoading() ? 'Loading…' : 'Load more' }}
      </button>
      }
      @if (personalError()) {
      <p class="insight-inline-error" role="alert">More results could not be loaded. Please try again.</p>
      }
      }
    </section>
    } @else {
    <section class="dash-container insight-panel" aria-live="polite">
      <div class="dash-chips" role="tablist" aria-label="Aggregate period">
        @for (period of periodFilters; track period.id) {
        <button type="button" class="dash-chip" [class.dash-chip--active]="activePeriod() === period.id"
          role="tab" [attr.aria-selected]="activePeriod() === period.id" (click)="setPeriod(period.id)">{{ period.label }}</button>
        }
      </div>

      @if (aggregateLoading()) {
      <div class="insight-empty"><span class="insight-spinner"></span><p>Loading campus trends…</p></div>
      } @else if (aggregateError()) {
      <div class="insight-empty insight-empty--error" role="alert">
        <mat-icon>cloud_off</mat-icon><h2>Campus trends are unavailable</h2><p>Check your connection and try again.</p>
        <button type="button" class="insight-action" (click)="loadAggregate()">Try again</button>
      </div>
      } @else if (aggregate(); as data) {
        @if (!data.summary.test_count && !hasTimelineData()) {
        <div class="insight-empty"><mat-icon>bar_chart</mat-icon><h2>No aggregate data for this period</h2>
          <p>Campus trends will appear when completed tests are available.</p></div>
        } @else {
        <div class="insight-aggregate__head">
          <div><h2 class="dash-section-head__title">Campus network trend</h2><p>{{ data.summary.test_count }} tests in the current period</p></div>
          <span class="spots-confidence spots-confidence--{{ confidenceLabel(data.summary.confidence).toLowerCase() }}">
            <span class="spots-confidence__dot"></span>{{ confidenceLabel(data.summary.confidence) }} confidence
          </span>
        </div>
        <div class="insight-chart-legend"><span><i class="download"></i>Download</span><span><i class="upload"></i>Upload</span></div>
        <p class="insight-chart-hint">Swipe the chart to see more periods ↔</p>
        <div class="insight-bar-chart" role="region" aria-label="Campus network trend chart. Scroll horizontally to view all periods." tabindex="0">
          @for (item of data.timeline; track item.date_range) {
          <div class="insight-bar-chart__col">
            <div class="insight-bar-chart__pair">
              <span class="insight-bar-chart__bar insight-bar-chart__bar--download"
                [style.height.%]="barHeight(item.download.avg)"
                tabindex="0" role="img"
                [attr.aria-label]="barTooltip('Average download', item.download.avg, item.date_range, item.test_count)"
                [matTooltip]="barTooltip('Average download', item.download.avg, item.date_range, item.test_count)"
                matTooltipPosition="above"></span>
              <span class="insight-bar-chart__bar insight-bar-chart__bar--upload"
                [style.height.%]="barHeight(item.upload.avg)"
                tabindex="0" role="img"
                [attr.aria-label]="barTooltip('Average upload', item.upload.avg, item.date_range, item.test_count)"
                [matTooltip]="barTooltip('Average upload', item.upload.avg, item.date_range, item.test_count)"
                matTooltipPosition="above"></span>
            </div>
            <span class="insight-bar-chart__tick">{{ item.label }}</span><small>{{ item.test_count }} tests</small>
          </div>
          }
        </div>
        <div class="dash-kpis insight-aggregate__kpis">
          <article class="dash-kpi"><span class="dash-kpi__label">Avg download</span><p class="dash-kpi__value">{{ formatSpeed(data.summary.avg_download_speed) }}<small>Mbps</small></p></article>
          <article class="dash-kpi"><span class="dash-kpi__label">Avg upload</span><p class="dash-kpi__value">{{ formatSpeed(data.summary.avg_upload_speed) }}<small>Mbps</small></p></article>
          <article class="dash-kpi"><span class="dash-kpi__label">Best spot</span><p class="dash-kpi__value dash-kpi__value--text">{{ data.summary.best_spot || 'No ranked spot' }}</p></article>
          <article class="dash-kpi"><span class="dash-kpi__label">Needs attention</span><p class="dash-kpi__value dash-kpi__value--text insight-aggregate__worst">{{ data.summary.worst_spot || 'No ranked spot' }}</p></article>
        </div>
        <p class="insight-generated">Updated {{ data.generated_at | date:'medium' }}. Confidence reflects sample size.</p>
        }
      }
    </section>
    }
  </div>
</app-main-content>
